import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { UsersSelectPaginated } from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { RecentActivityItem } from '@/modules/admin-dashboard/components/dashboard/RecentActivity.tsx';
import { useTasksActivityLogsQuery } from '@/modules/admin-dashboard/queries/tasks.ts';
import { CalendarInput, CalendarInputProps, EmptyListState, PageLayout, Select, Spinner } from 'ui';
import { SelectPaginateOption } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { ActivityType } from 'compliance-api';

const SYSTEM_FILTER_VALUE = 0;
const SYSTEM_FIXED_OPTION: SelectPaginateOption<number> = {
  value: SYSTEM_FILTER_VALUE,
  label: 'System',
};

const auditLogActivitySelectOptions = Object.values(ActivityType).map(type => ({
  label: fixNameEnum(type),
  value: type,
}));

interface AuditLogFilters {
  dateFilter: null | [string, string];
  typeFilter: ActivityType | null;
  userFilter: SelectPaginateOption<number> | null;
  recipientFilter: SelectPaginateOption<number> | null;
}

const AuditLog = () => {
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<AuditLogFilters>({
    dateFilter: null,
    typeFilter: null,
    userFilter: null,
    recipientFilter: null,
  });

  useEffect(() => {
    setFilters({
      dateFilter: null,
      typeFilter: null,
      userFilter: null,
      recipientFilter: null,
    });
  }, [businessId]);
  const { dateFilter, typeFilter, userFilter, recipientFilter } = filters;

  const isSystemFilter = userFilter?.value === SYSTEM_FILTER_VALUE;

  const { data, isFetching, isLoading, hasNextPage, fetchNextPage } = useTasksActivityLogsQuery({
    page: 1,
    page_size: 30,
    ...(typeFilter ? { type: typeFilter } : {}),
    ...(dateFilter
      ? {
          from_date: dateFilter[0],
          to_date: dayjs(dateFilter[1]).add(1, 'day').format('YYYY-MM-DD'),
        }
      : {}),
    ...(userFilter && !isSystemFilter ? ({ filter_by_user_id: userFilter.value } as object) : {}),
    ...(isSystemFilter ? ({ filter_by_system: true } as object) : {}),
    ...(recipientFilter ? ({ filter_by_recipient_user_id: recipientFilter.value } as object) : {}),
  } as Parameters<typeof useTasksActivityLogsQuery>[0]);

  const activities = (data?.pages || []).flatMap(({ data }) => data);

  const handleChangeDateFilter: CalendarInputProps['rangeOnChange'] = dates => {
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      setFilters(filters => ({
        ...filters,
        dateFilter: start_date && end_date ? [start_date, end_date] : null,
      }));
      return;
    }
    setFilters(filters => ({ ...filters, dateFilter: null }));
  };

  const handleChangeUserFilter = useCallback(
    (userFilter: SelectPaginateOption<number> | null) => {
      setFilters(filters => ({ ...filters, userFilter }));
    },
    [setFilters],
  );

  const handleChangeRecipientFilter = useCallback(
    (recipientFilter: SelectPaginateOption<number> | null) => {
      setFilters(filters => ({ ...filters, recipientFilter }));
    },
    [setFilters],
  );

  const handleChangeTypeFilter = useCallback(
    (typeFilter: ActivityType | null) => {
      setFilters(filters => ({ ...filters, typeFilter }));
    },
    [setFilters],
  );

  const headerAction = (
    <div className="flex gap-4 justify-end items-end flex-wrap">
      <div>
        <CalendarInput
          range
          disabled={isFetching}
          rangeOnChange={handleChangeDateFilter}
          rangeValue={dateFilter ? [dayjs(dateFilter[0]), dayjs(dateFilter[1])] : undefined}
          className="w-max-[150px] [&>div]:py-[9px]"
        />
      </div>
      <div className="min-w-[180px]">
        <UsersSelectPaginated
          isMulti={false}
          isClearable
          disabled={isFetching}
          placeholder="Triggered by"
          value={userFilter}
          onChange={handleChangeUserFilter}
          fixedOption={SYSTEM_FIXED_OPTION}
        />
      </div>
      <div className="min-w-[180px]">
        <UsersSelectPaginated
          isMulti={false}
          isClearable
          disabled={isFetching}
          placeholder="Email recipient"
          value={recipientFilter}
          onChange={handleChangeRecipientFilter}
        />
      </div>
      <div className="min-w-[150px]">
        <Select
          isClearable
          disabled={isFetching}
          placeholder="Activity type"
          options={auditLogActivitySelectOptions}
          onChange={handleChangeTypeFilter}
          value={typeFilter}
        />
      </div>
    </div>
  );

  return (
    <PageLayout title="Audit log" headerAction={headerAction}>
      <EmptyListState
        title="No audit log yet"
        description={
          'To create a type, click New Event Type (e.g. Breach event, Training event). ' +
          'Event types are needed for your business employees to report occurring events.'
        }
        isLoading={isLoading}
        itemsCount={activities.length}
      />
      {isLoading && (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isLoading && !!activities.length && (
        <InfiniteScroll
          className="flex flex-col gap-2"
          dataLength={activities.length}
          hasMore={!!hasNextPage}
          // style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
          next={fetchNextPage}
        >
          {activities.map(({ type, data, created_at, id, created_with_ai }) => (
            <RecentActivityItem
              data={data}
              created_at={created_at}
              type={type}
              key={id}
              created_with_ai={created_with_ai}
            />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(AuditLog, [Role.COMPLIANCE_ADMIN, Role.COMPLIANCE_USER]);
